import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  AlertTriangle, Check, ChevronDown, ChevronRight, FileText, Loader2, Lock,
  Pencil, Plus, RefreshCw, Sparkles, Trash2, Users, X,
} from "lucide-react";
import api, { formatApiError } from "../api";
import i18n from "../i18n";
import { can, useAuth } from "../auth";
import {
  Badge, Button, Field, Input, Modal, Select, Spinner, Textarea, Toast,
} from "../components/common";
import TranscriptDropzone from "../components/TranscriptDropzone";

// Self-terminating refresh while one meeting is being extracted. This is not a
// second app-wide poller (CLAUDE.md forbids that -- NotificationContext owns the
// global polling): it runs only on this page, only while this meeting's state is
// `processing`, and stops the moment it isn't.
const PROCESSING_POLL_MS = 4000;

function fmtDateTime(value) {
  if (!value) return "—";
  return new Date(value).toLocaleString(i18n.language, {
    year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit",
  });
}

function toLocalInput(value) {
  if (!value) return "";
  const d = new Date(value);
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export default function MeetingDetail() {
  const { id } = useParams();
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();

  const [meeting, setMeeting] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [toast, setToast] = useState(null);
  const [busy, setBusy] = useState(false);

  const [editingSummary, setEditingSummary] = useState(false);
  const [summaryDraft, setSummaryDraft] = useState("");
  const [acceptingId, setAcceptingId] = useState(null);
  const [acceptDraft, setAcceptDraft] = useState({ title: "", owner_id: "", due_date: "" });

  const [transcript, setTranscript] = useState(null);
  const [transcriptOpen, setTranscriptOpen] = useState(false);
  const [metaOpen, setMetaOpen] = useState(false);
  const [metaDraft, setMetaDraft] = useState(null);
  const [directory, setDirectory] = useState([]);
  const pollRef = useRef(null);

  const writable = can.write(user);
  const fail = (e) => setToast({
    type: "error", message: formatApiError(e.response?.data?.detail) || e.message,
  });

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/meetings/${id}`);
      setMeeting(r.data);
      return r.data;
    } catch (e) {
      if (e.response?.status === 404) setNotFound(true);
      else fail(e);
      return null;
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/users/directory").then((r) => setDirectory(r.data)).catch(() => {}); }, []);

  // Poll only while processing; clear on every state change and on unmount.
  useEffect(() => {
    if (meeting?.state !== "processing") {
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
      return undefined;
    }
    pollRef.current = setInterval(load, PROCESSING_POLL_MS);
    return () => { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } };
  }, [meeting?.state, load]);

  const payload = meeting?.extraction?.payload || {};
  const actionItems = payload.action_items || [];
  const acceptedCount = actionItems.filter((i) => i.state === "accepted").length;

  async function act(fn) {
    setBusy(true);
    try {
      const r = await fn();
      if (r?.data) setMeeting(r.data);
      return r;
    } catch (e) {
      fail(e);
      return null;
    } finally {
      setBusy(false);
    }
  }

  async function openTranscript() {
    setTranscriptOpen((v) => !v);
    if (transcript || transcriptOpen) return;
    try {
      const r = await api.get(`/meetings/${id}/transcript`);
      setTranscript(r.data);
    } catch (e) {
      fail(e);
    }
  }

  async function saveMeta() {
    const body = {
      subject: metaDraft.subject,
      starts_at: metaDraft.starts_at ? new Date(metaDraft.starts_at).toISOString() : null,
      ends_at: metaDraft.ends_at ? new Date(metaDraft.ends_at).toISOString() : null,
      direction: metaDraft.direction || null,
      sensitive: metaDraft.sensitive,
      participants: metaDraft.participants.map((p) => ({
        contact_id: p.contact_id || null, user_id: p.user_id || null,
        display_name: p.display_name || p.name || null,
        speaker_label: p.speaker_label || null, is_internal: !!p.is_internal,
      })),
    };
    const r = await act(() => api.put(`/meetings/${id}`, body));
    if (r) { setMetaOpen(false); setToast({ type: "success", message: t("meet.saved") }); }
  }

  if (notFound) {
    return (
      <div className="space-y-4">
        <p className="text-sm text-muted">{t("meet.notFound")}</p>
        <Link to="/meetings" className="text-primary text-sm">{t("meet.backToList")}</Link>
      </div>
    );
  }
  if (!meeting) return <Spinner />;

  const startMetaEdit = () => {
    setMetaDraft({
      subject: meeting.subject,
      starts_at: toLocalInput(meeting.starts_at),
      ends_at: toLocalInput(meeting.ends_at),
      direction: meeting.direction || "",
      sensitive: !!meeting.sensitive,
      participants: [...(meeting.participants || [])],
    });
    setMetaOpen(true);
  };

  return (
    <div className="space-y-5 pb-6">
      {/* ---------- header ---------- */}
      <div className="space-y-3">
        <Link to="/meetings" className="text-xs text-muted hover:text-txt transition-colors">
          ← {t("meet.backToList")}
        </Link>
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="min-w-0">
            <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight flex items-center gap-2 flex-wrap">
              {meeting.subject}
              {meeting.sensitive && <Lock size={18} className="text-amber-600" />}
            </h1>
            <div className="text-sm text-muted mt-1.5 flex items-center gap-3 flex-wrap">
              <span>{fmtDateTime(meeting.starts_at || meeting.created_at)}</span>
              {meeting.deal_id && (
                <Link to={`/deals/${meeting.deal_id}`} className="text-primary hover:underline">
                  {meeting.deal_title || t("meet.deal")}
                </Link>
              )}
              {meeting.company_name && <span>{meeting.company_name}</span>}
              <Badge value={meeting.state} label={t(`meet.state.${meeting.state}`)} />
            </div>
          </div>
          {writable && (
            <div className="flex items-center gap-2">
              <Button variant="ghost" onClick={startMetaEdit} data-testid="meet-edit-btn">
                <Pencil size={15} /> {t("common.edit")}
              </Button>
              <Button
                variant="ghost"
                data-testid="meet-delete-btn"
                onClick={async () => {
                  if (!window.confirm(t("meet.confirmDelete"))) return;
                  const r = await act(() => api.delete(`/meetings/${id}`));
                  if (r) navigate("/meetings");
                }}
              >
                <Trash2 size={15} />
              </Button>
            </div>
          )}
        </div>

        {(meeting.participants || []).length > 0 && (
          <div className="flex items-center gap-2 flex-wrap">
            <Users size={14} className="text-muted" />
            {meeting.participants.map((p) => (
              <span
                key={p.id}
                className={`text-xs px-2 py-0.5 rounded-sm border ${
                  p.is_internal ? "border-primary/40 text-primary" : "border-border text-muted"
                }`}
              >
                {p.name || p.display_name}
              </span>
            ))}
          </div>
        )}
      </div>

      {/* ---------- state banners ---------- */}
      {meeting.state === "processing" && (
        <div className="border border-border rounded-sm p-4 flex items-center gap-3 text-sm">
          <Loader2 size={16} className="animate-spin text-primary shrink-0" />
          <span>{t("meet.processingHint")}</span>
        </div>
      )}
      {meeting.state === "failed" && (
        <div className="border border-danger/40 rounded-sm p-4 space-y-2">
          <div className="flex items-center gap-2 text-sm text-danger">
            <AlertTriangle size={16} className="shrink-0" />
            <span className="font-medium">{t("meet.failedTitle")}</span>
          </div>
          {meeting.extraction?.error && (
            <p className="text-xs text-muted break-words">{meeting.extraction.error}</p>
          )}
          <p className="text-sm text-muted">{t("meet.failedHint")}</p>
        </div>
      )}
      {!meeting.content_visible && (
        <div className="border border-amber-500/40 rounded-sm p-4 text-sm flex items-center gap-2">
          <Lock size={16} className="text-amber-600 shrink-0" />
          {t("meet.sensitiveHidden")}
        </div>
      )}
      {meeting.state === "draft" && !meeting.has_transcript && meeting.content_visible && (
        <div className="border border-border rounded-sm p-4 space-y-3">
          <p className="text-sm font-medium">{t("meet.noTranscriptYet")}</p>
          {writable && (
            <TranscriptDropzone meetingId={id} compact onUploaded={(m) => setMeeting(m)} />
          )}
        </div>
      )}

      {/* ---------- summary ---------- */}
      {meeting.content_visible && meeting.summary && (
        <section className="border border-border rounded-sm p-5 space-y-3">
          <div className="flex items-center justify-between gap-3">
            <h2 className="font-display font-bold flex items-center gap-2">
              <Sparkles size={16} className="text-primary" /> {t("meet.summary")}
            </h2>
            {writable && !editingSummary && (
              <Button
                variant="ghost"
                data-testid="edit-summary-btn"
                onClick={() => { setSummaryDraft(meeting.summary); setEditingSummary(true); }}
              >
                <Pencil size={14} /> {t("common.edit")}
              </Button>
            )}
          </div>
          {editingSummary ? (
            <div className="space-y-2">
              <Textarea rows={8} value={summaryDraft} onChange={(e) => setSummaryDraft(e.target.value)} />
              <div className="flex justify-end gap-2">
                <Button variant="ghost" onClick={() => setEditingSummary(false)}>{t("common.cancel")}</Button>
                <Button
                  disabled={busy}
                  onClick={async () => {
                    const r = await act(() => api.put(`/meetings/${id}/summary`, { summary: summaryDraft }));
                    if (r) setEditingSummary(false);
                  }}
                >
                  {t("common.save")}
                </Button>
              </div>
            </div>
          ) : (
            <p className="text-sm leading-relaxed whitespace-pre-wrap">{meeting.summary}</p>
          )}
          {meeting.extraction?.edited_at && (
            <p className="text-xs text-muted">{t("meet.humanEdited")}</p>
          )}
        </section>
      )}

      {/* ---------- action items ---------- */}
      {meeting.content_visible && actionItems.length > 0 && (
        <section className="border border-border rounded-sm p-5 space-y-3" data-testid="action-items">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <h2 className="font-display font-bold">{t("meet.actionItems")}</h2>
            {acceptedCount > 0 && (
              <span className="text-xs text-muted">
                {t("meet.alreadyAccepted", { count: acceptedCount })}
              </span>
            )}
          </div>
          <div className="divide-y divide-border">
            {actionItems.map((item) => (
              <div key={item.id} className="py-3 space-y-2" data-testid={`action-item-${item.id}`}>
                <div className="flex items-start gap-3">
                  <span
                    className={`w-2 h-2 rounded-full mt-1.5 shrink-0 ${
                      item.state === "accepted" ? "bg-success"
                        : item.state === "dismissed" ? "bg-slate-400" : "bg-primary"
                    }`}
                  />
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm ${item.state === "dismissed" ? "line-through text-muted" : ""}`}>
                      {item.title}
                    </p>
                    <div className="text-xs text-muted mt-0.5 flex items-center gap-2 flex-wrap">
                      {item.owner_hint && <span>{item.owner_hint}</span>}
                      {item.due_hint && <span>{item.due_hint}</span>}
                      <Badge value={item.state === "accepted" ? "accepted" : item.state}
                             label={t(`meet.itemState.${item.state}`)} />
                      {item.activity_id && (
                        <Link to="/activities" className="text-primary hover:underline">
                          {t("meet.viewTask")}
                        </Link>
                      )}
                    </div>
                  </div>
                  {writable && item.state === "proposed" && acceptingId !== item.id && (
                    <div className="flex items-center gap-1 shrink-0">
                      <Button
                        variant="ghost"
                        data-testid={`accept-${item.id}`}
                        onClick={() => {
                          setAcceptingId(item.id);
                          setAcceptDraft({ title: item.title, owner_id: "", due_date: "" });
                        }}
                      >
                        <Check size={14} /> {t("meet.accept")}
                      </Button>
                      <Button
                        variant="ghost"
                        data-testid={`dismiss-${item.id}`}
                        disabled={busy}
                        onClick={() => act(() => api.post(`/meetings/${id}/action-items/${item.id}/dismiss`))}
                      >
                        <X size={14} />
                      </Button>
                    </div>
                  )}
                </div>

                {acceptingId === item.id && (
                  <div className="ml-5 border-l-2 border-primary/30 pl-4 space-y-2">
                    <Input
                      value={acceptDraft.title}
                      onChange={(e) => setAcceptDraft({ ...acceptDraft, title: e.target.value })}
                    />
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      <Select
                        value={acceptDraft.owner_id}
                        onChange={(e) => setAcceptDraft({ ...acceptDraft, owner_id: e.target.value })}
                      >
                        <option value="">{t("meet.ownerDefault")}</option>
                        {directory.map((u) => (
                          <option key={u.id} value={u.id}>{u.name || u.email}</option>
                        ))}
                      </Select>
                      <Input
                        type="date"
                        value={acceptDraft.due_date}
                        onChange={(e) => setAcceptDraft({ ...acceptDraft, due_date: e.target.value })}
                      />
                    </div>
                    <div className="flex justify-end gap-2">
                      <Button variant="ghost" onClick={() => setAcceptingId(null)}>{t("common.cancel")}</Button>
                      <Button
                        disabled={busy || !acceptDraft.title.trim()}
                        data-testid={`confirm-accept-${item.id}`}
                        onClick={async () => {
                          const r = await act(() => api.post(
                            `/meetings/${id}/action-items/${item.id}/accept`,
                            {
                              title: acceptDraft.title,
                              owner_id: acceptDraft.owner_id || null,
                              due_date: acceptDraft.due_date
                                ? new Date(`${acceptDraft.due_date}T12:00:00`).toISOString() : null,
                            },
                          ));
                          if (r) {
                            setAcceptingId(null);
                            setToast({ type: "success", message: t("meet.taskCreated") });
                          }
                        }}
                      >
                        {t("meet.createTask")}
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </section>
      )}

      {/* ---------- decisions / risks / topics ---------- */}
      {meeting.content_visible && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {[["decisions", payload.decisions], ["risks", payload.risks]].map(([key, rows]) => (
            (rows || []).length > 0 && (
              <section key={key} className="border border-border rounded-sm p-5 space-y-2">
                <h2 className="font-display font-bold">{t(`meet.${key}`)}</h2>
                <ul className="space-y-1.5">
                  {rows.map((r) => (
                    <li key={r.id} className="text-sm flex gap-2">
                      <span className="text-muted shrink-0">·</span>
                      <span>{r.text}</span>
                    </li>
                  ))}
                </ul>
              </section>
            )
          ))}
        </div>
      )}
      {meeting.content_visible && (payload.topics || []).length > 0 && (
        <div className="flex items-center gap-2 flex-wrap">
          {payload.topics.map((topic) => (
            <span key={topic} className="text-xs px-2 py-0.5 rounded-sm bg-surface border border-border text-muted">
              {topic}
            </span>
          ))}
        </div>
      )}

      {/* ---------- second depth: transcript, provenance, regenerate ---------- */}
      {meeting.content_visible && meeting.has_transcript && (
        <section className="border border-border rounded-sm">
          <button
            onClick={openTranscript}
            data-testid="toggle-transcript"
            className="w-full flex items-center justify-between gap-2 px-5 py-4 hover:bg-surface transition-colors"
          >
            <span className="font-display font-bold flex items-center gap-2">
              <FileText size={16} className="text-muted" /> {t("meet.transcript")}
            </span>
            {transcriptOpen ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
          </button>
          {transcriptOpen && (
            <div className="px-5 pb-5 space-y-3">
              {!transcript ? <Spinner /> : (
                <>
                  <p className="text-xs text-muted">
                    {transcript.original_filename || t("meet.pastedTitle")} · {transcript.char_count} {t("meet.chars")} · {transcript.source}
                  </p>
                  <pre className="text-xs whitespace-pre-wrap bg-surface border border-border rounded-sm p-3 max-h-96 overflow-y-auto">
                    {transcript.raw_text}
                  </pre>
                </>
              )}
              <div className="flex items-center justify-between gap-3 flex-wrap pt-1">
                <p className="text-xs text-muted">
                  {meeting.extraction
                    ? `${meeting.extraction.model_name || "—"} · prompt v${meeting.extraction.prompt_version}`
                    : "—"}
                </p>
                {writable && (
                  <Button
                    variant="ghost"
                    disabled={busy || meeting.state === "processing"}
                    data-testid="regenerate-btn"
                    onClick={() => act(() => api.post(`/meetings/${id}/extract`))}
                  >
                    <RefreshCw size={14} /> {t("meet.regenerate")}
                  </Button>
                )}
              </div>
              {writable && (
                <div className="pt-2 border-t border-border">
                  <p className="text-xs text-muted mb-2">{t("meet.replaceTranscript")}</p>
                  <TranscriptDropzone meetingId={id} compact onUploaded={(m) => { setMeeting(m); setTranscript(null); }} />
                </div>
              )}
            </div>
          )}
        </section>
      )}

      {/* ---------- edit modal ---------- */}
      <Modal
        open={metaOpen}
        onClose={() => setMetaOpen(false)}
        title={t("meet.editTitle")}
        footer={
          <>
            <Button variant="ghost" onClick={() => setMetaOpen(false)}>{t("common.cancel")}</Button>
            <Button onClick={saveMeta} disabled={busy}>{t("common.save")}</Button>
          </>
        }
      >
        {metaDraft && (
          <>
            <Field label={t("meet.subject")}>
              <Input value={metaDraft.subject} onChange={(e) => setMetaDraft({ ...metaDraft, subject: e.target.value })} />
            </Field>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field label={t("meet.startsAt")}>
                <Input type="datetime-local" value={metaDraft.starts_at}
                       onChange={(e) => setMetaDraft({ ...metaDraft, starts_at: e.target.value })} />
              </Field>
              <Field label={t("meet.endsAt")}>
                <Input type="datetime-local" value={metaDraft.ends_at}
                       onChange={(e) => setMetaDraft({ ...metaDraft, ends_at: e.target.value })} />
              </Field>
            </div>
            <Field label={t("meet.direction")}>
              <Select value={metaDraft.direction}
                      onChange={(e) => setMetaDraft({ ...metaDraft, direction: e.target.value })}>
                <option value="inbound">{t("meet.dirInbound")}</option>
                <option value="outbound">{t("meet.dirOutbound")}</option>
                <option value="internal">{t("meet.dirInternal")}</option>
              </Select>
            </Field>

            <div className="space-y-2">
              <span className="text-xs uppercase tracking-[0.15em] text-muted">{t("meet.participants")}</span>
              <div className="flex flex-wrap gap-2">
                {metaDraft.participants.map((p, idx) => (
                  <span key={p.id || `${p.display_name}-${idx}`}
                        className="text-xs px-2 py-1 rounded-sm border border-border flex items-center gap-1.5">
                    {p.name || p.display_name}
                    <button onClick={() => setMetaDraft({
                      ...metaDraft,
                      participants: metaDraft.participants.filter((_, i) => i !== idx),
                    })}>
                      <X size={12} className="text-muted hover:text-danger" />
                    </button>
                  </span>
                ))}
              </div>
              <Select
                value=""
                onChange={(e) => {
                  const u = directory.find((x) => x.id === e.target.value);
                  if (!u) return;
                  setMetaDraft({
                    ...metaDraft,
                    participants: [...metaDraft.participants,
                      { user_id: u.id, name: u.name || u.email, is_internal: true }],
                  });
                }}
              >
                <option value="">{t("meet.addTeammate")}</option>
                {directory.map((u) => <option key={u.id} value={u.id}>{u.name || u.email}</option>)}
              </Select>
              {/* AI-proposed names from the transcript: one click to keep them,
                  which is the whole point of extracting metadata as well. */}
              {(payload.proposed_meta?.participants || []).length > 0 && (
                <div className="flex flex-wrap gap-2 pt-1">
                  {payload.proposed_meta.participants.map((p) => (
                    <button
                      key={p.name}
                      onClick={() => setMetaDraft({
                        ...metaDraft,
                        participants: [...metaDraft.participants, {
                          display_name: p.name, speaker_label: p.speaker_label,
                          is_internal: !!p.is_internal, name: p.name,
                        }],
                      })}
                      className="text-xs px-2 py-1 rounded-sm border border-dashed border-border text-muted hover:border-primary hover:text-primary transition-colors flex items-center gap-1"
                    >
                      <Plus size={11} /> {p.name}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <label className="flex items-start gap-2.5 pt-1 cursor-pointer">
              <input
                type="checkbox"
                checked={metaDraft.sensitive}
                data-testid="sensitive-toggle"
                onChange={(e) => setMetaDraft({ ...metaDraft, sensitive: e.target.checked })}
                className="mt-0.5"
              />
              <span className="text-sm">
                {t("meet.sensitive")}
                <span className="block text-xs text-muted">{t("meet.sensitiveHint")}</span>
              </span>
            </label>
          </>
        )}
      </Modal>

      <Toast toast={toast} onClose={() => setToast(null)} />
    </div>
  );
}
